// Extracted from original/src/components/ExcessTrustModal.tsx (whole file, 406 lines; ADR-08 §2.1, D-003). Used by the
// old Currency Vault (CurrencyVaultView.tsx:3668-3696, sale / withdrawal on a trust above its balance, D02 R-16) and the
// old quick operation (App.tsx:48067-48095, deposit on a trust above its balance); in the new portal also by Dealing in
// Dollar customer mode (old D01 had no excess dialog and let the trust go negative, F-247; D-218 shared dialog).
// Kept verbatim: backdrop + frame, amber header, the four-figure summary, the four options (reduce / another
// customer's trust / cash / as debt) with their searchable pickers ("+ أضف حساب جديد" first, then the register), the
// new-account name inputs and the footer button. Cut: `lang` / inline ar-en strings → catalogue keys `excess.*`
// (old texts copied); `formatNumber` + raw numbers → preformatted labels from the container (no arithmetic here);
// `debts` (the old name-keyed register, R-15) → `trustOptions` / `debtOptions` with account ids (D-017); the selected
// value pairs `selectedX` / `setSelectedX` / `newXName` / `setNewXName` / `showXDropdown` → internal UI state; the
// four handlers → callbacks that receive the resolved choice (`ExcessChoice`: an existing account id, or a new
// account name), or `null` when nothing valid was chosen (the container shows the old "select first" toast).
import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useT } from '../i18n-context.js';

/** One account of the register shown in a picker (balance preformatted in the dialog's currency). */
export interface ExcessAccountOption {
  readonly id: string;
  readonly name: string;
  readonly balanceLabel?: string;
}

/** What the operator chose in a picker: an existing account (container key) or "+ add new account" with a name. */
export type ExcessChoice = { readonly kind: 'existing'; readonly id: string } | { readonly kind: 'new'; readonly name: string };

export interface ExcessTrustModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly customerName: string;
  /** Preformatted figures (old `excessTrustData.trustAmount / depositedAmount / excessAmount`). */
  readonly trustAmountLabel: string;
  readonly requiredAmountLabel: string;
  readonly excessAmountLabel: string;
  /** Currency label (old `currency` prop: 'د.ع' or '$'). */
  readonly currencyLabel: string;
  readonly title?: string;
  readonly description?: string;
  readonly actionTypeLabel?: string;
  /** Trust accounts of other customers in the dialog's currency (old filter: type أمانة, name ≠ the customer). */
  readonly trustOptions: readonly ExcessAccountOption[];
  /** Debt accounts in the dialog's currency (old filter: type دين). */
  readonly debtOptions: readonly ExcessAccountOption[];
  /** Pre-selection of the debt picker (old: the customer's own name, App.tsx:6229 / CurrencyVaultView.tsx:1739). */
  readonly defaultDebt?: { readonly id: string; readonly name: string } | null;
  readonly onReduce: () => void;
  readonly onPayFromOtherTrust: (choice: ExcessChoice | null) => void;
  readonly onCash: () => void;
  readonly onAsDebt: (choice: ExcessChoice | null) => void;
}

/** Shared picker state of the old dialogs: the text in the input, the picked id, and the new-account name. */
export function usePick(initial?: { readonly id: string; readonly name: string } | null) {
  const [text, setText] = useState(initial?.name ?? '');
  const [id, setId] = useState(initial?.id ?? '');
  const [newName, setNewName] = useState('');
  const [open, setOpen] = useState(false);
  return { text, setText, id, setId, newName, setNewName, open, setOpen };
}

/** Resolves a picker into the choice handed to the container (text typed but not picked → exact name match or null). */
export function resolvePick(p: ReturnType<typeof usePick>, addNew: string, options: readonly ExcessAccountOption[]): ExcessChoice | null {
  if (p.text === addNew) return p.newName.trim() ? { kind: 'new', name: p.newName.trim() } : null;
  if (p.id) return { kind: 'existing', id: p.id };
  const exact = options.filter((o) => o.name.trim().toLowerCase() === p.text.trim().toLowerCase());
  return exact.length === 1 ? { kind: 'existing', id: exact[0]!.id } : null;
}

export function ExcessTrustModal(props: ExcessTrustModalProps) {
  const t = useT();
  const second = usePick();
  const debt = usePick(props.defaultDebt ?? null);
  if (!props.open) return null;

  const curr = props.currencyLabel || 'د.ع';
  const addNew = t('excess.add_new_account');
  const trust = props.trustAmountLabel;
  const excess = props.excessAmountLabel;

  const modalTitle = props.title || t('excess.trust.title');
  const modalDesc = props.description || t('excess.trust.description');

  const matches = (p: ReturnType<typeof usePick>, name: string) => !p.text || p.text === addNew || name.toLowerCase().includes(p.text.toLowerCase());

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4 pb-16 sm:pb-4 overflow-hidden" data-testid="excess-trust-modal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={props.onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-[#0d1117] w-full max-w-xl rounded-2xl sm:rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] max-h-[84vh] sm:max-h-[88vh] flex flex-col text-right"
          dir="rtl"
        >
          <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-amber-50 dark:bg-amber-950/20 shrink-0">
            <h3 className="text-lg sm:text-xl font-black flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <span>⚠️</span>
              {modalTitle}
            </h3>
            <button
              onClick={props.onClose}
              className="text-gray-400 hover:text-gray-650 dark:hover:text-[#f0f6fc] transition-colors p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-[#30363d]"
            >
              <X size={20}/>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            <div className="bg-amber-50/40 dark:bg-amber-950/5 border border-amber-200 dark:border-amber-900/30 rounded-2xl p-4 sm:p-5 space-y-3">
              <p className="text-gray-650 dark:text-gray-300 text-sm font-semibold leading-relaxed">
                {modalDesc}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-2">
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.customer')}</span>
                  <span className="font-bold text-gray-800 dark:text-white text-sm">{props.customerName}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.trust.balance')}</span>
                  <span className="font-mono font-bold text-gray-800 dark:text-white text-sm text-amber-600">{trust} {curr}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{props.actionTypeLabel || t('excess.trust.required')}</span>
                  <span className="font-mono font-bold text-gray-800 dark:text-white text-sm text-rose-600">{props.requiredAmountLabel} {curr}</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block mb-1">{t('excess.trust.remaining')}</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm shrink-0 bg-emerald-50 dark:bg-emerald-950/20 px-2.5 py-0.5 rounded-lg border border-emerald-100 dark:border-emerald-900/30">{excess} {curr}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-black text-gray-750 dark:text-gray-200 text-right">
                {t('excess.trust.choose')}
              </h4>

              <div className="grid grid-cols-1 gap-3">
                {/* Option 1: Edit Input / Reduce */}
                <button
                  type="button"
                  data-testid="excess-trust-reduce"
                  onClick={props.onReduce}
                  className="p-4 bg-gray-50 hover:bg-gray-100 dark:bg-[#161b22] dark:hover:bg-[#21262d] border border-gray-200 dark:border-[#30363d] rounded-2xl text-right transition-all flex items-start gap-3 hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
                >
                  <span className="text-2xl mt-0.5">✏️</span>
                  <div>
                    <span className="block font-black text-sm text-gray-800 dark:text-white">
                      {t('excess.trust.reduce_title')}
                    </span>
                    <span className="block text-xs text-gray-450 dark:text-gray-400 mt-1">
                      {t('excess.trust.reduce_desc', { trust, curr })}
                    </span>
                  </div>
                </button>

                {/* Option 2: Complete Remaining from another Customer's Trust */}
                <div className="border border-blue-200/50 dark:border-blue-900/30 rounded-2xl p-4 bg-blue-50/30 dark:bg-blue-950/10 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">👥</span>
                    <div>
                      <span className="block font-black text-sm text-blue-800 dark:text-blue-400">
                        {t('excess.trust.other_title')}
                      </span>
                      <span className="block text-xs text-blue-650 dark:text-blue-500 mt-1">
                        {t('excess.trust.other_desc', { trust, excess, curr })}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-blue-100 dark:border-blue-900/20">
                    <label className="block text-xs font-bold text-gray-500 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                      {t('excess.trust.other_label')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={second.text || ''}
                        onFocus={() => second.setOpen(true)}
                        onChange={(e) => {
                          second.setText(e.target.value);
                          second.setId('');
                          second.setOpen(true);
                        }}
                        className="w-full bg-white dark:bg-[#05070a] border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                        placeholder={t('excess.search_client')}
                        autoComplete="off"
                      />

                      {second.open && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => second.setOpen(false)} />
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-2xl shadow-2xl z-50 max-h-[160px] overflow-y-auto custom-scrollbar text-right">
                            <div className="p-1 space-y-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  second.setText(addNew);
                                  second.setId('');
                                  second.setOpen(false);
                                }}
                                className="w-full text-right px-4 py-2 text-xs font-black text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl transition-colors flex items-center gap-1.5"
                              >
                                <span>{addNew}</span>
                              </button>

                              {props.trustOptions
                                .filter((d) => matches(second, d.name))
                                .map((d) => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => {
                                      second.setText(d.name);
                                      second.setId(d.id);
                                      second.setOpen(false);
                                    }}
                                    className="w-full flex justify-between items-center px-4 py-2 text-xs font-bold text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#161b22] rounded-xl transition-colors gap-3"
                                  >
                                    <span className="truncate text-right flex-1">{d.name}</span>
                                    <span className="font-mono text-[10px] text-gray-500 dark:text-gray-450 shrink-0 bg-gray-50 dark:bg-[#161b22]/80 px-2 py-0.5 rounded-lg">
                                      {d.balanceLabel ?? '0'} {curr}
                                    </span>
                                  </button>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {second.text === addNew && (
                      <div className="mt-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                        <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">
                          {t('excess.new_second_name_label')}
                        </label>
                        <input
                          required
                          type="text"
                          value={second.newName || ''}
                          onChange={(e) => second.setNewName(e.target.value)}
                          className="w-full bg-white dark:bg-[#05070a] border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                          placeholder={t('excess.new_name_placeholder')}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="excess-trust-other"
                      onClick={() => props.onPayFromOtherTrust(resolvePick(second, addNew, props.trustOptions))}
                      className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs shadow-lg shadow-blue-500/10 active:scale-95 cursor-pointer"
                    >
                      <span>✅</span>
                      {t('excess.trust.other_confirm')}
                    </button>
                  </div>
                </div>

                {/* Option 3: Receive Remaining Cash */}
                <button
                  type="button"
                  data-testid="excess-trust-cash"
                  onClick={props.onCash}
                  className="p-4 bg-emerald-50/50 hover:bg-emerald-100/50 dark:bg-emerald-950/10 dark:hover:bg-emerald-900/10 border border-emerald-200/50 dark:border-emerald-900/30 rounded-2xl text-right transition-all flex items-start gap-3 hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
                >
                  <span className="text-2xl mt-0.5">💵</span>
                  <div>
                    <span className="block font-black text-sm text-emerald-800 dark:text-emerald-400">
                      {t('excess.trust.cash_title')}
                    </span>
                    <span className="block text-xs text-emerald-650 dark:text-emerald-500 mt-1">
                      {t('excess.trust.cash_desc', { trust, excess, curr })}
                    </span>
                  </div>
                </button>

                {/* Option 4: Add Remaining as Debt to Customer */}
                <div className="border border-purple-200/50 dark:border-purple-900/30 rounded-2xl p-4 bg-purple-50/30 dark:bg-purple-950/10 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">🛡️</span>
                    <div>
                      <span className="block font-black text-sm text-purple-800 dark:text-purple-400">
                        {t('excess.trust.debt_title')}
                      </span>
                      <span className="block text-xs text-purple-650 dark:text-purple-500 mt-1">
                        {t('excess.trust.debt_desc', { trust, excess, curr })}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-purple-100 dark:border-purple-900/20">
                    <label className="block text-xs font-bold text-gray-500 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                      {t('excess.trust.debt_label')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={debt.text || ''}
                        onFocus={() => debt.setOpen(true)}
                        onChange={(e) => {
                          debt.setText(e.target.value);
                          debt.setId('');
                          debt.setOpen(true);
                        }}
                        className="w-full bg-white dark:bg-[#05070a] border border-purple-200 dark:border-purple-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                        placeholder={t('excess.trust.debt_search')}
                        autoComplete="off"
                      />

                      {debt.open && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => debt.setOpen(false)} />
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-2xl shadow-2xl z-50 max-h-[160px] overflow-y-auto custom-scrollbar text-right">
                            <div className="p-1 space-y-0.5">
                              <button
                                type="button"
                                onClick={() => {
                                  debt.setText(addNew);
                                  debt.setId('');
                                  debt.setOpen(false);
                                }}
                                className="w-full text-right px-4 py-2 text-xs font-black text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl transition-colors flex items-center gap-1.5"
                              >
                                <span>{addNew}</span>
                              </button>

                              {props.debtOptions
                                .filter((d) => matches(debt, d.name))
                                .map((d) => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => {
                                      debt.setText(d.name);
                                      debt.setId(d.id);
                                      debt.setOpen(false);
                                    }}
                                    className="w-full flex justify-between items-center px-4 py-2 text-xs font-bold text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#161b22] rounded-xl transition-colors gap-3"
                                  >
                                    <span className="truncate text-right flex-1">{d.name}</span>
                                    <span className="font-mono text-[10px] text-gray-500 dark:text-gray-450 shrink-0 bg-gray-50 dark:bg-[#161b22]/80 px-2 py-0.5 rounded-lg">
                                      {d.balanceLabel ?? '0'} {curr}
                                    </span>
                                  </button>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {debt.text === addNew && (
                      <div className="mt-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
                        <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">
                          {t('excess.trust.debt_new_label')}
                        </label>
                        <input
                          required
                          type="text"
                          value={debt.newName || ''}
                          onChange={(e) => debt.setNewName(e.target.value)}
                          className="w-full bg-white dark:bg-[#05070a] border border-purple-200 dark:border-purple-900/40 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:text-[#f0f6fc] font-black text-sm text-right"
                          placeholder={t('excess.new_name_placeholder')}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="excess-trust-debt"
                      onClick={() => props.onAsDebt(resolvePick(debt, addNew, props.debtOptions))}
                      className="w-full mt-3 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs shadow-lg shadow-purple-500/10 active:scale-95 cursor-pointer"
                    >
                      <span>✅</span>
                      {t('excess.trust.debt_confirm')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-gray-100 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#090d13] flex justify-end shrink-0">
            <button
              type="button"
              onClick={props.onClose}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-750 text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all text-xs active:scale-[0.98]"
            >
              {t('excess.close')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
